import { ParticipantView, QuestionView, SectionView } from '@views';
import { useSaveSurveyResponsesAction } from '@actions';
import { useEffect, useState } from 'react';
import { useNextSurveySectionViewCreator } from '@app/view-creator/next-survey-section-view-creator';
import { EmptyFunction } from '@utils/common-types';
import { useAllCohortUsersViewCreator } from '@viewCreators/active-participants-view-creator';
import { useNavigationEligibility } from '@components/SurveyDrawer/NavigationEligibilityContextProvider';
import { useAppCookies } from '@app/providers/AppCookieProvider';

export type SurveyDrawerController = {
  surveyTitle: string;
  isDrawerOpen: boolean;
  activeStep: number;
  currentQuestion: QuestionView;
  questions: QuestionView[];
  handleNext: EmptyFunction;
  handlePrev: EmptyFunction;
  saveResponses: () => void;
  isSaving: boolean;
  refresh: () => Promise<any>;
  activeParticipants: ParticipantView[];
};

/**
 * The drawer records answers by writing onto the ResponseView objects it renders, so the
 * section it works from has to be ours alone — sharing the query cache's copy means any
 * refetch swaps in fresh, blank objects and quietly discards everything answered so far.
 */
const cloneSection = (section: SectionView): SectionView => JSON.parse(JSON.stringify(section)) as SectionView;

export const useSurveyDrawerController = (groupId: number): SurveyDrawerController => {
  const { data, refetch } = useNextSurveySectionViewCreator(groupId);
  const [workingSection, setWorkingSection] = useState<SectionView | null>(null);
  const [activeStep, setActiveStep] = useState<number>(0);
  const { mutateAsync: saveSurveyAction, isPending: isSaving } = useSaveSurveyResponsesAction();
  const [currentQuestion, setCurrentQuestion] = useState<QuestionView>(null as unknown as QuestionView);
  const [questions, setQuestions] = useState<QuestionView[]>([]);
  const [surveyTitle, setSurveyTitle] = useState<string>('');
  const { data: userData } = useAllCohortUsersViewCreator(groupId);
  const { setSectionRef, setCurrentStep, triggerEligibility } = useNavigationEligibility();
  const environment = import.meta.env.VITE_APP_ENV || 'development';
  const cookies = useAppCookies();
  const currentUserId = cookies.profile.id;
  const currentUser = userData?.allActiveCohortUsers.find((p) => p.CohortUser.userId === currentUserId);

  // Take a private copy of whatever section the server is serving, but only when it is a
  // section we are not already working on. Re-adopting the section in progress — which any
  // refetch would do — is what blanked out answers and submitted empty responses.
  useEffect(() => {
    if (!data) return;
    const incoming = data.section;
    if (!incoming || incoming.questions.length === 0) {
      setWorkingSection(null);
      return;
    }
    setWorkingSection((current) => {
      if (current && current.id === incoming.id) {
        return current; // already working on this section — keep it, answers and all
      }
      return cloneSection(incoming); // a section we haven't started — take a private copy
    });
  }, [data]);

  useEffect(() => {
    if (!workingSection) return;
    setSectionRef(workingSection);
    setSurveyTitle(workingSection.name);

    const allQuestions = workingSection.questions ?? [];
    setQuestions(allQuestions);

    const step = Math.min(activeStep, allQuestions.length - 1);
    setCurrentQuestion(allQuestions[step]);
    triggerEligibility();
  }, [workingSection, activeStep]);

  // Show the drawer if:
  //   The survey has at least one section
  //   That section has questions
  let isDrawerOpen = !!workingSection && workingSection.questions.length > 0;

  // // If the user is a coach, close the drawer (no survey)
  if (currentUser?.CohortUser.role === 'Coach') {
    isDrawerOpen = false;
  }

  // If the user is a captain or admin, and we're *not* on a development environment, close the drawer (no survey)
  if (
    (currentUser?.CohortUser.role === 'Captain' || currentUser?.CohortUser.user.role === 'Admin') &&
    !['development', 'dev', 'test', 'local', 'staging'].includes(environment)
  ) {
    isDrawerOpen = false;
  }

  const handleNext = () => {
    setActiveStep((prev) => {
      const next = prev + 1;
      setCurrentStep(next);
      return next;
    });
  };

  const handlePrev = () => {
    setActiveStep((prev) => {
      const next = prev - 1;
      setCurrentStep(next);
      return next;
    });
  };

  const handleSaveResponses = async () => {
    if (isSaving) return;
    if (data && workingSection) {
      await saveSurveyAction({ section: workingSection, groupId, surveyId: data.surveyId });
    }
    setActiveStep(0);
    // The eligibility provider tracks the step separately and only resets itself when the
    // section id changes; without this it stays pointed at the last question of the section
    // just finished, which leaves Finish visible over the next section's first question.
    setCurrentStep(0);
    await refetch();
  };
  const activeParticipants = userData?.activeParticipants ?? [];
  return {
    isDrawerOpen,
    questions,
    activeStep,
    surveyTitle,
    currentQuestion,
    handleNext,
    handlePrev,
    saveResponses: handleSaveResponses,
    isSaving,
    refresh: refetch,
    activeParticipants,
  };
};
